import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import deJson from 'primelocale/de.json';
import enJson from 'primelocale/en.json';
import { PrimeNGConfig } from 'primeng/api';
import { Nullable } from 'primeng/ts-helpers';
import { I18NObject } from '../../shared/contract/i18n-object.contract';
import { Languages } from '../../shared/enums/languages.enum';
import { WhitelabelManagerService } from '../../whitelabel/services/whitelabel-manager.service';
import { UserConfigurationService } from './user-configuration.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private whitelabelOverrides: Record<string, string> = {};

  constructor(
    private translate: TranslateService,
    private userConfigurationService: UserConfigurationService,
    private primengConfig: PrimeNGConfig,
    private whitelabelManagerService: WhitelabelManagerService,
  ) {
    this.whitelabelOverrides = this.whitelabelManagerService.getWhitelabelCustomization()?.translationKeyOverrides || {};
  }

  public getCurrentLanguage(): string {
    return this.translate.currentLang;
  }

  public getCurrentLocale(): string {
    return this.translate.currentLang === Languages.DEV ? Languages.EN : this.translate.currentLang;
  }

  public setDefaultLanguage(languages: Languages): void {
    this.translate.setDefaultLang(languages);
  }

  public getInstant(key: string, interpolateParams?: object): string {
    key = this.whitelabelOverrides[key] ?? key;
    return key ? this.translate.instant(key, interpolateParams) : '';
  }

  public getInstantWithoutFallBack(key: string, interpolateParams?: object): string {
    key = this.whitelabelOverrides[key] ?? key;
    const fallBackLang = this.translate.getDefaultLang();
    this.translate.setDefaultLang(this.translate.currentLang);

    let translation = this.translate.instant(key, interpolateParams);
    translation = key === translation ? '' : translation;
    this.translate.setDefaultLang(fallBackLang);
    return translation;
  }

  public use(language: Languages | string) {
    this.setPrimeNgTranslation(language); // Set primelocale translation used internally in primeng components
    return this.translate.use(language);
  }

  public resolveI18NObject(
    value: I18NObject | string | Nullable,
    language?: Languages | Nullable,
    fallback?: string,
  ): string {
    if (!value) {
      return fallback ?? '';
    }

    if (typeof value === 'string') {
      return value ?? fallback ?? '';
    }

    if (language) {
      return value[language] ?? '';
    }

    return (
      value[this.userConfigurationService.getUserLanguage()] ??
      value[this.userConfigurationService.getFallbackLanguage()] ??
      fallback ??
      Object.values(value)?.[0] ?? // Takes the first existing translation
      ''
    );
  }

  private setPrimeNgTranslation(language: Languages | string): void {
    let translation;
    // Switch case is necessary because the strings won't match. i.e.: Languages.EN is 'en-us' but in primelocale it is 'en'
    switch (language) {
      case Languages.DE:
        translation = deJson.de;
        break;
      case Languages.EN:
      default:
        translation = enJson.en;
        break;
    }

    this.primengConfig.setTranslation(translation);
  }
}
